// pages/purchase-returns/edit.jsx
import { useEffect, useMemo, useRef, useState } from "react"
import { useLocation, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom"
import nProgress from "nprogress"
import toast from "react-hot-toast"
import { Transition } from "components"
import { useAuth } from "contexts/auth-context"
import { getUser } from "helpers/auth-helper"
import Form from "./form"

function todayISO() {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
}

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

// ✅ fetch helper konsisten
async function apiFetch(path, { method = "GET", body } = {}) {
  const base = (process.env.REACT_APP_INVENTORY_API_URL || "").replace(/\/$/, "")
  const url = `${base}${path}`

  // Sisipkan user_id dari sesi login UI (sumber data yang sama dengan useAuth())
  // ke body request tulis, supaya backend bisa mengisi created_by/updated_by.
  // Tanpa ini actor di backend jatuh ke "SYSTEM".
  const userId = getUser()?.id
  const payload = method !== "GET" && userId ? { user_id: userId, ...(body || {}) } : body

  const res = await fetch(url, {
    method,
    headers: { "Content-Type": "application/json" },
    body: payload ? JSON.stringify(payload) : null,
  })

  const json = await res.json().catch(() => ({}))
  if (!res.ok) {
    const msg = json?.message || "Request gagal"
    const err = json?.error ? `: ${json.error}` : ""
    throw new Error(msg + err)
  }
  return json
}

export default function EditPurchaseReturn() {
  const { id } = useParams() // /purchase-returns/:id/edit
  const isEdit = Boolean(id)

  const { can, currentBranch } = useAuth()
  const { setTitle } = useOutletContext()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // create mode biasanya dipanggil lewat /purchase-returns/create?receipt_id=...
  const receiptIdFromQuery = searchParams.get("receipt_id") || null

  const [ret, setRet] = useState(null)
  const [errors, setErrors] = useState({})
  const [acting, setActing] = useState(false)

  const initRef = useRef(false)

  const status = String(ret?.status || "DRAFT").toUpperCase()
  const isDraft = status === "DRAFT"
  const isLocked = !isDraft

  useEffect(() => {
    if (initRef.current) return
    initRef.current = true

    setTitle?.(isEdit ? `Edit Purchase Return ${id}` : "Create Purchase Return")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // =========
  // Load data (edit mode)
  // =========
  useEffect(() => {
    const run = async () => {
      if (!isEdit) return
      try {
        nProgress.start()
        const res = await apiFetch(`/purchase-returns/${id}`) // kalau backend kamu belum ada show, bilang, nanti aku adjust.
        const data = res?.data || res
        setRet(data)
      } catch (e) {
        toast.error(e?.message || "Gagal load purchase return")
      } finally {
        nProgress.done()
      }
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id])

  // =========
  // Create draft otomatis dari receipt_id (create mode)
  // =========
  useEffect(() => {
    const run = async () => {
      if (isEdit) return
      if (!receiptIdFromQuery) return

      try {
        nProgress.start()
        toast.loading("Membuat draft return...", { id: "pr-create" })

        // outlet_id int (pakai currentBranch.id)
        const res = await apiFetch(`/goods-receipts/${receiptIdFromQuery}/purchase-returns`, {
          method: "POST",
          body: {
            outlet_id: currentBranch?.id ?? null,
            return_date: todayISO(),
          },
        })

        const data = res?.data || res
        toast.success("Draft return dibuat", { id: "pr-create" })

        // pindah ke edit route biar URL rapi
        if (data?.id) {
          navigate(`/purchase-returns/${data.id}/edit`, { replace: true, state: location.state })
        } else {
          setRet(data)
        }
      } catch (e) {
        toast.error(e?.message || "Gagal membuat draft return", { id: "pr-create" })
      } finally {
        nProgress.done()
      }
    }
    run()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, receiptIdFromQuery, currentBranch?.id])

  // =========
  // Builder + Validation
  // =========
  const buildItemsPayload = (form) => {
    const items = Array.isArray(form?.items) ? form.items : Array.isArray(ret?.items) ? ret.items : []
    return {
      items: items
        .map((it) => ({
          receipt_item_id: it.receipt_item_id,
          qty_returned: num(it.qty_returned),
          unit_cost: it.unit_cost === "" || it.unit_cost === null || it.unit_cost === undefined ? null : num(it.unit_cost),
          notes: it.notes ?? null,
        }))
        // drop qty 0 biar backend bersih
        .filter((x) => num(x.qty_returned) >= 0),
    }
  }

  const validate = (payload) => {
    const e = {}

    // ret wajib ada dulu (draft header)
    if (!ret?.id) e.header = "Return belum dibuat. Pilih Goods Receipt dulu."

    const items = Array.isArray(payload?.items) ? payload.items : []
    if (!items.length) e.items = "Minimal 1 item"

    // qty harus bilangan bulat (karena stock_transaction.quantity INT)
    for (let i = 0; i < items.length; i++) {
      const it = items[i]
      if (!it.receipt_item_id) e[`items.${i}.receipt_item_id`] = "receipt_item_id wajib"
      const q = num(it.qty_returned)
      if (q < 0) e[`items.${i}.qty_returned`] = "Qty tidak boleh < 0"
      if (Math.abs(q - Math.round(q)) > 1e-9) e[`items.${i}.qty_returned`] = "Qty harus bilangan bulat"
    }

    setErrors(e)
    return Object.keys(e).length === 0
  }

  // =========
  // Actions
  // =========
  const handleSaveDraft = async (form) => {
    if (!ret?.id) return
    if (isLocked) return

    const payload = buildItemsPayload(form)
    if (!validate(payload)) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Saving draft...", { id: "pr-save" })

      const res = await apiFetch(`/purchase-returns/${ret.id}/items`, {
        method: "POST",
        body: payload,
      })

      const next = res?.data || res
      toast.success(res?.message || "Draft tersimpan", { id: "pr-save" })
      if (next?.id) setRet(next)
    } catch (e) {
      toast.error(e?.message || "Gagal save draft", { id: "pr-save" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const handlePost = async () => {
    if (!ret?.id) return
    if (!can?.("purchase-return.post")) return
    if (isLocked) return

    if (!window.confirm(`POST Return ${ret?.return_number || ""}?`)) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Posting...", { id: "pr-post" })

      const res = await apiFetch(`/purchase-returns/${ret.id}/post`, { method: "POST" })
      const next = res?.data || res

      toast.success(res?.message || "Return berhasil dipost", { id: "pr-post" })
      setRet(next?.id ? next : (prev) => ({ ...(prev || {}), status: "POSTED" }))
    } catch (e) {
      toast.error(e?.message || "Gagal post return", { id: "pr-post" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const handleCancel = async () => {
    if (!ret?.id) return
    if (!can?.("purchase-return.cancel")) return
    if (status === "CANCELLED") return

    const reason = window.prompt("Alasan cancel (optional):", "")
    if (reason === null) return

    try {
      setActing(true)
      nProgress.start()
      toast.loading("Cancelling...", { id: "pr-cancel" })

      const res = await apiFetch(`/purchase-returns/${ret.id}/cancel`, {
        method: "POST",
        body: reason ? { reason } : null,
      })
      const next = res?.data || res

      toast.success(res?.message || "Return dibatalkan", { id: "pr-cancel" })
      setRet(next?.id ? next : (prev) => ({ ...(prev || {}), status: "CANCELLED" }))
    } catch (e) {
      toast.error(e?.message || "Gagal cancel return", { id: "pr-cancel" })
    } finally {
      nProgress.done()
      setActing(false)
    }
  }

  const actions = useMemo(
    () => ({
      canSave: Boolean(can?.("purchase-return.edit") || can?.("purchase-return.create")),
      canPost: Boolean(can?.("purchase-return.post")) && status === "DRAFT",
      canCancel: Boolean(can?.("purchase-return.cancel")) && status === "POSTED",

      isLocked,
      acting,

      onSaveDraft: handleSaveDraft,
      onPost: handlePost,
      onCancel: handleCancel,
      onBack: () => navigate("/purchase-returns", { state: location.state }),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [can, status, isLocked, acting, ret?.id]
  )

  return (
    <Transition type="fade">
      <Form
        data={ret}
        errors={errors}
        actions={actions}
        // save draft triggered by header button; form submit also calls save draft
        onSubmit={handleSaveDraft}
      />
    </Transition>
  )
}